import React, { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { get, isEmpty, isEqual } from 'lodash';

import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import { getLobDescription } from '~/Utils/lobUtils';

import useLobOptions from '../../../TPA/LOB/hooks/useLobOptions';
import ArrayMultiselectFieldWithChipsFormik from '../../ArrayMultiselectFieldWithChipsFormik';
import useFormikChangeListener from '../FormikChangeListener';

const ALL_ID = 0;
const ALL_OPTION = {
  id: ALL_ID,
  label: 'All (inc. future lobs)',
  value: ALL_ID,
};

interface LobMultiSelectWithChipsFormikProps {
  lobsFieldId: string;
  label?: string;
  showOnly?: boolean;
  disabled?: boolean;
  subOrganizationIds?: number[];
  filterLobsFunc?: () => boolean;
  isAllLobs?: boolean;
  allLobsFieldId?: string;
  subOrgFieldId?: string;
  shouldResetOnSubOrgChange?: boolean;
  disableAllOptionExpansion?: boolean;
  // Callers that need expansion most of the time but want a one-shot bypass
  // (e.g. a Clear button that resets the form) can set this ref to `true`
  // immediately before the change that should not expand; the listener
  // consumes it once and resets it to `false`.
  suppressNextExpansionRef?: React.MutableRefObject<boolean>;
}

interface LobOption {
  id: string;
  label: string;
  value: string;
}

interface UseLobOptionsReturn {
  lobOptions: string[];
  isLoading: boolean;
  isLoaded: boolean;
  isError: boolean;
}

const LobMultiSelectWithChipsFormik: React.FC<LobMultiSelectWithChipsFormikProps> = ({
  lobsFieldId,
  label = 'Lines of Business',
  showOnly = false,
  disabled = false,
  subOrganizationIds = [],
  filterLobsFunc,
  isAllLobs = false,
  allLobsFieldId,
  subOrgFieldId,
  // Dialog test suites that stub this component reproduce the reset this default turns on, so their flows
  // stay reachable in the app. Flipping the default leaves those suites passing while the app behaves
  // differently — grep the tests that mock this module and revisit them alongside the change.
  shouldResetOnSubOrgChange = true,
  disableAllOptionExpansion = false,
  suppressNextExpansionRef,
}) => {
  const { setFieldValue, setFieldTouched, values } = useFormikContext();
  // Armed by the sub-org reset below so the expansion listener can tell that listener's programmatic
  // clear apart from a user unpicking "All". Kept separate from `suppressNextExpansionRef`: that one
  // belongs to callers, and sharing a single one-shot would let an external bypass swallow an internal
  // reset (or the reverse).
  const isSubOrgResetRef = useRef(false);
  const { lobConfigurationsDict } = useLobConfiguration();
  const { lobOptions } = useLobOptions({
    subOrganizationIds,
    filterLobsFunc,
    isAllLobs,
  }) as UseLobOptionsReturn;

  const lobs: LobOption[] =
    lobOptions?.map((lob: string) => ({
      id: lob,
      label: getLobDescription(lob, lobConfigurationsDict),
      value: lob,
    })) || [];

  // set all lobs when all lobs is selected
  useFormikChangeListener({
    listenForKeys: [lobsFieldId],
    onChange: (newValues: Record<string, unknown>) => {
      const lobsValues = get(newValues, lobsFieldId);
      if (!allLobsFieldId) {
        return;
      }

      // Check if lobsValues is an array before calling includes
      const isAllSelected = Array.isArray(lobsValues) && lobsValues.includes(ALL_ID);
      setFieldValue(allLobsFieldId, isAllSelected);
    },
    runOnFirstRender: true,
  });

  // When the user unpicks the "All" sentinel and leaves an empty selection,
  // expand to every individual LOB so the dropdown still feels "select all".
  // Callers that want a one-shot bypass (e.g. Clear all filters in the
  // DocumentTypes filter) set suppressNextExpansionRef.current = true right
  // before the reset; the listener consumes it once. The sub-org reset below
  // uses its own one-shot for the same reason: only a *user* unpicking "All"
  // should expand, and that listener's clear looks identical from here.
  useFormikChangeListener({
    listenForKeys: [lobsFieldId],
    onChange: (newValues: Record<string, unknown>, previousValues: Record<string, unknown>) => {
      const currentList = (get(newValues, lobsFieldId) as (string | number)[]) ?? [];
      const previousList = (get(previousValues, lobsFieldId) as (string | number)[]) ?? [];
      const wentFromAllToEmpty = previousList.includes(ALL_ID) && !currentList.includes(ALL_ID) && isEmpty(currentList);
      if (!wentFromAllToEmpty) return;
      // Both one-shots are armed for *this* transition, so both are consumed here before deciding.
      // Returning on the first one alone would leave the other armed to swallow the next genuine
      // unpick, which a caller that arms its bypass and changes the sub-org in one interaction hits.
      // Consumed above the `disableAllOptionExpansion` guard for the same reason: under that prop
      // there is no expansion to skip, but an un-consumed one-shot would outlive the transition it
      // was armed for and swallow the next genuine unpick if the prop is ever lifted.
      const wasSubOrgReset = isSubOrgResetRef.current;
      isSubOrgResetRef.current = false;
      const wasSuppressedByCaller = suppressNextExpansionRef?.current ?? false;
      if (suppressNextExpansionRef) {
        suppressNextExpansionRef.current = false;
      }
      if (disableAllOptionExpansion) return;
      if (wasSubOrgReset || wasSuppressedByCaller) return;
      const individualLobIds = lobOptions ?? [];
      if (!isEmpty(individualLobIds)) {
        setFieldValue(lobsFieldId, individualLobIds);
      }
    },
    runOnFirstRender: false,
  });

  // set on first render the initial value of all lobs
  useEffect(() => {
    if (allLobsFieldId) {
      const allLobs = get(values, allLobsFieldId, false);
      if (allLobs) {
        setFieldValue(lobsFieldId, [ALL_ID]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reset all lobs when sub org changes
  useFormikChangeListener({
    listenForKeys: subOrgFieldId ? [subOrgFieldId] : [],
    onChange: (newValues: Record<string, unknown>, previousValues: Record<string, unknown>) => {
      if (!subOrgFieldId) {
        return;
      }

      const previousSubOrg = get(previousValues, subOrgFieldId);
      const currentSubOrg = get(newValues, subOrgFieldId);
      if (!shouldResetOnSubOrgChange || isEqual(previousSubOrg, currentSubOrg)) {
        return;
      }

      // Arm only when the value being cleared is the "All" sentinel, i.e. exactly the transition the
      // expansion listener reacts to. Arming unconditionally would leave the one-shot set whenever
      // clearing an already-empty selection produced no change for that listener to consume, and the
      // next genuine "All" unpick would be swallowed instead.
      const lobsValues = get(newValues, lobsFieldId);
      if (Array.isArray(lobsValues) && lobsValues.includes(ALL_ID)) {
        isSubOrgResetRef.current = true;
      }
      if (allLobsFieldId) {
        setFieldValue(allLobsFieldId, false);
      }
      setFieldValue(lobsFieldId, []);
      setFieldTouched(lobsFieldId, false);
    },
    runOnFirstRender: true,
  });

  return (
    <ArrayMultiselectFieldWithChipsFormik
      showOnly={showOnly}
      disabled={disabled}
      id={lobsFieldId}
      label={label}
      options={lobs}
      allOption={ALL_OPTION}
    />
  );
};

export const isAllLobsSelected = (ids: (number | string)[]): boolean => ids.includes(ALL_OPTION.id);

/** The selection minus the "All" sentinel — the concrete LOB keys a caller may send to the
 *  server or the catalog. The sentinel is presentation state; `is_all_lobs` is the wire
 *  spelling of "all", so it must never leave the form as a LOB named `0`. */
export const concreteSelectedLobs = (ids: (number | string)[]): string[] =>
  ids.filter((id): id is string => typeof id === 'string');

// The "All (inc. future lobs)" sentinel, attached as a static the way
// `SubOrgMultiSelectWithChipsFormik.ALL_OPTION` already is, so dialogs can seed an
// all-LOBs rule's initial value in this widget's own representation. Seeding the
// server shape (`lobs: [], is_all_lobs: true`) instead makes the mount conversion
// above rewrite both fields, which any change listener keyed on them reads as a
// user edit — the sub-org field never had that bug precisely because its dialogs
// seed `[ALL_OPTION.id]`.
export default Object.assign(LobMultiSelectWithChipsFormik, { ALL_OPTION });
